"use client";

import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { X, CheckCircle2, XCircle, Info, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

type NotificationType = "success" | "error" | "info" | "warning";

interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  duration?: number;
}

interface NotificationContextValue {
  success: (message: string, duration?: number) => void;
  error: (message: string, duration?: number) => void;
  info: (message: string, duration?: number) => void;
  warning: (message: string, duration?: number) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  const addNotification = useCallback((type: NotificationType, message: string, duration = 5000) => {
    const id = Math.random().toString(36).substring(2, 11);
    const notification: Notification = { id, type, message, duration };

    setNotifications((prev) => [...prev, notification]);

    if (duration > 0) {
      setTimeout(() => {
        setNotifications((prev) => prev.filter((n) => n.id !== id));
      }, duration);
    }
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const value: NotificationContextValue = {
    success: (message, duration) => addNotification("success", message, duration),
    error: (message, duration) => addNotification("error", message, duration),
    info: (message, duration) => addNotification("info", message, duration),
    warning: (message, duration) => addNotification("warning", message, duration),
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 pointer-events-none">
        {notifications.map((notification) => (
          <NotificationItem
            key={notification.id}
            notification={notification}
            onClose={() => removeNotification(notification.id)}
          />
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

function NotificationItem({
  notification,
  onClose,
}: {
  notification: Notification;
  onClose: () => void;
}) {
  const icons = {
    success: CheckCircle2,
    error: XCircle,
    info: Info,
    warning: AlertCircle,
  };

  const Icon = icons[notification.type];

  return (
    <div
      className={cn(
        "pointer-events-auto min-w-[320px] max-w-md rounded-lg shadow-lg border",
        "animate-in slide-in-from-right-full duration-300",
        "bg-background/95 backdrop-blur-sm",
        {
          "border-green-500/50 dark:border-green-500/30": notification.type === "success",
          "border-red-500/50 dark:border-red-500/30": notification.type === "error",
          "border-blue-500/50 dark:border-blue-500/30": notification.type === "info",
          "border-amber-500/50 dark:border-amber-500/30": notification.type === "warning",
        }
      )}
    >
      <div className="flex items-start gap-3 p-4">
        <div
          className={cn("mt-0.5 flex-shrink-0", {
            "text-green-600 dark:text-green-400": notification.type === "success",
            "text-red-600 dark:text-red-400": notification.type === "error",
            "text-blue-600 dark:text-blue-400": notification.type === "info",
            "text-amber-600 dark:text-amber-400": notification.type === "warning",
          })}
        >
          <Icon className="size-5" />
        </div>
        <p className="flex-1 text-sm font-medium leading-relaxed">{notification.message}</p>
        <button
          onClick={onClose}
          className="flex-shrink-0 rounded-md p-1 hover:bg-muted transition-colors"
          aria-label="Close notification"
        >
          <X className="size-4 text-muted-foreground" />
        </button>
      </div>
    </div>
  );
}
